import React, { useEffect, useState } from 'react';
import ConfirmModal from './ConfirmModal';
import {
  TenantAdminLogin,
  getTenantAdminLogins,
  createTenantAdminLogin,
  updateTenantAdminLogin,
  deleteTenantAdminLogin,
} from '../services/api';

interface TenantAdminLoginsPanelProps {
  tenantId: string;
  tenantName: string;
  onClose: () => void;
}

const emptyForm = {
  name: '',
  email: '',
  password: '',
  isEnabled: true,
};

const TenantAdminLoginsPanel: React.FC<TenantAdminLoginsPanelProps> = ({
  tenantId,
  tenantName,
  onClose,
}) => {
  const [logins, setLogins] = useState<TenantAdminLogin[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState<TenantAdminLogin | null>(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<TenantAdminLogin | null>(null);
  const [showPassword, setShowPassword] = useState(false);
  const [validationErrors, setValidationErrors] = useState({ email: '', password: '' });

  const validateEmail = (email: string): string => {
    if (!email.trim()) return 'Email is required';
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email.trim())) return 'Invalid email format';
    return '';
  };

  const validatePassword = (password: string, isEdit: boolean): string => {
    if (isEdit && !password.trim()) return '';
    if (!password.trim()) return 'Password is required';
    if (password.length < 8) return 'Password must be at least 8 characters';
    return '';
  };

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await getTenantAdminLogins(tenantId);
      setLogins(res.data.data || []);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load admin logins');
      setLogins([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantId]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      e.preventDefault();
      const activeElement = document.activeElement as HTMLElement | null;
      if (activeElement && typeof activeElement.blur === 'function') {
        activeElement.blur();
      }
      if (deleteTarget) {
        setDeleteTarget(null);
        return;
      }
      onClose();
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [deleteTarget, onClose]);

  const openCreate = () => {
    setEditing(null);
    setForm(emptyForm);
    setShowPassword(false);
    setError('');
    setValidationErrors({ email: '', password: '' });
    setShowForm(true);
  };

  const openEdit = (login: TenantAdminLogin) => {
    setEditing(login);
    setForm({
      name: login.metadata.name || '',
      email: login.spec.email,
      password: '',
      isEnabled: login.spec.isEnabled !== false,
    });
    setShowPassword(false);
    setError('');
    setValidationErrors({ email: '', password: '' });
    setShowForm(true);
  };

  const closeForm = () => {
    setShowForm(false);
    setEditing(null);
    setForm(emptyForm);
    setShowPassword(false);
    setError('');
    setValidationErrors({ email: '', password: '' });
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    const emailErr = validateEmail(form.email);
    const passwordErr = validatePassword(form.password, !!editing);
    setValidationErrors({ email: emailErr, password: passwordErr });
    if (emailErr || passwordErr) return;

    setSaving(true);
    setError('');
    try {
      if (editing) {
        await updateTenantAdminLogin(editing._id, {
          metadata: { name: form.name.trim() || undefined },
          spec: {
            email: form.email.trim().toLowerCase(),
            ...(form.password.trim() ? { password: form.password } : {}),
            isEnabled: form.isEnabled,
          },
        });
      } else {
        await createTenantAdminLogin({
          metadata: { name: form.name.trim() || undefined },
          spec: {
            tenantId,
            email: form.email.trim().toLowerCase(),
            password: form.password,
            isEnabled: form.isEnabled,
          },
        });
      }
      closeForm();
      await load();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to save admin login');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await deleteTenantAdminLogin(deleteTarget._id);
      setDeleteTarget(null);
      await load();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to delete admin login');
      setDeleteTarget(null);
    }
  };

  const headerTitle = showForm
    ? editing
      ? 'Edit Admin Login'
      : 'New Admin Login'
    : tenantName || 'Tenant Admin Logins';

  return (
    <div className="drawer-overlay" onClick={onClose}>
      <div className="drawer" onClick={(e) => e.stopPropagation()}>
        <div className="drawer-header">
          <h3 className="drawer-title">{headerTitle}</h3>
          <button type="button" className="drawer-close" onClick={onClose} aria-label="Close">
            ×
          </button>
        </div>

        {showForm ? (
          <form onSubmit={handleSave} className="drawer-form">
            <div className="drawer-body">
              {error && <div className="error-message">{error}</div>}

              <div className="form-group">
                <label>Display name</label>
                <input
                  type="text"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="Optional"
                />
              </div>
              <div className="form-group">
                <label>
                  Email <span className="required-star">*</span>
                </label>
                <input
                  type="email"
                  value={form.email}
                  onChange={(e) => {
                    const value = e.target.value;
                    setForm({ ...form, email: value });
                    setValidationErrors({ ...validationErrors, email: validateEmail(value) });
                  }}
                  placeholder="admin@example.com"
                  className={validationErrors.email ? 'input-error' : ''}
                />
                {validationErrors.email && (
                  <small style={{ color: '#e74c3c', fontSize: '14px', marginTop: '4px', display: 'block' }}>
                    {validationErrors.email}
                  </small>
                )}
              </div>
              <div className="form-group">
                <label>
                  Password {editing ? '(leave blank to keep)' : <span className="required-star">*</span>}
                </label>
                <div style={{ position: 'relative' }}>
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={form.password}
                    onChange={(e) => {
                      const value = e.target.value;
                      setForm({ ...form, password: value });
                      setValidationErrors({
                        ...validationErrors,
                        password: validatePassword(value, !!editing),
                      });
                    }}
                    placeholder={editing ? 'Leave blank to keep current' : 'Minimum 8 characters'}
                    className={validationErrors.password ? 'input-error' : ''}
                    style={{ paddingRight: '45px' }}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((v) => !v)}
                    style={{
                      position: 'absolute',
                      right: '10px',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                      padding: '5px',
                      fontSize: '20px',
                    }}
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                  >
                    {showPassword ? '👁️' : '👁️‍🗨️'}
                  </button>
                </div>
                {validationErrors.password && (
                  <small style={{ color: '#e74c3c', fontSize: '14px', marginTop: '4px', display: 'block' }}>
                    {validationErrors.password}
                  </small>
                )}
              </div>
              <div
                className="form-group"
                style={{ display: 'flex', alignItems: 'center', gap: '8px', flexDirection: 'row' }}
              >
                <input
                  id="tal-enabled"
                  type="checkbox"
                  checked={form.isEnabled}
                  onChange={(e) => setForm({ ...form, isEnabled: e.target.checked })}
                  style={{ width: 'auto', margin: 0 }}
                />
                <label htmlFor="tal-enabled" style={{ margin: 0 }}>
                  Enabled (can login)
                </label>
              </div>
            </div>
            <div className="drawer-footer">
              <button type="button" className="btn-cancel" onClick={closeForm} disabled={saving}>
                Cancel
              </button>
              <button type="submit" className="btn-submit" disabled={saving}>
                {saving ? (
                  <>
                    <span className="loading-spinner btn-spinner" aria-hidden />
                    Saving...
                  </>
                ) : (
                  'Save'
                )}
              </button>
            </div>
          </form>
        ) : (
          <div className="drawer-form">
            <div className="drawer-body">
              {error && <div className="error-message">{error}</div>}

              {loading ? (
                <div className="loading">Loading...</div>
              ) : logins.length === 0 ? (
                <div className="empty-state">
                  <p>No tenant admin logins yet. Add credentials so users can log in as Admin for this tenant.</p>
                </div>
              ) : (
                <div className="table-container">
                  <table className="bookings-table">
                    <thead>
                      <tr>
                        <th>Name</th>
                        <th>Email</th>
                        <th>Status</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {logins.map((login) => (
                        <tr key={login._id}>
                          <td>{login.metadata.name || '—'}</td>
                          <td>{login.spec.email}</td>
                          <td>
                            <span
                              className={`status-badge status-${login.spec.isEnabled ? 'active' : 'inactive'}`}
                            >
                              {login.spec.isEnabled ? 'Enabled' : 'Disabled'}
                            </span>
                          </td>
                          <td>
                            <div className="action-buttons">
                              <button
                                type="button"
                                className="btn-icon btn-edit"
                                onClick={() => openEdit(login)}
                              >
                                Edit
                              </button>
                              <button
                                type="button"
                                className="btn-icon btn-delete"
                                onClick={() => setDeleteTarget(login)}
                              >
                                Delete
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
            <div className="drawer-footer">
              <button type="button" className="btn-cancel" onClick={onClose}>
                Cancel
              </button>
              <button type="button" className="btn-add" onClick={openCreate}>
                + Add Admin Login
              </button>
            </div>
          </div>
        )}
      </div>

      <ConfirmModal
        isOpen={!!deleteTarget}
        title="Delete Admin Login"
        message={
          deleteTarget
            ? `Delete admin login for ${deleteTarget.spec.email}? They will no longer be able to sign in.`
            : ''
        }
        confirmText="Delete"
        cancelText="Cancel"
        onConfirm={handleDelete}
        onCancel={() => setDeleteTarget(null)}
        type="danger"
      />
    </div>
  );
};

export default TenantAdminLoginsPanel;
